import { Download, FileText, CheckSquare, ShieldCheck } from "lucide-react";

type Props = {
  /** Visual variant: "feature" = full hero block (hub), "compact" = inline article footer */
  variant?: "feature" | "compact";
};

const PDF_HREF = "/downloads/colorado-local-seo-checklist.pdf";

export function LeadMagnetCTA({ variant = "compact" }: Props) {
  if (variant === "feature") {
    return (
      <section
        aria-label="Free Colorado Local SEO Checklist"
        className="bg-gradient-dark py-16 sm:py-20"
      >
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="grid items-center gap-10 rounded-3xl border border-brand-gold/30 bg-brand-black/60 p-7 shadow-elegant sm:p-10 lg:grid-cols-[1fr_auto] lg:gap-14">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-gold">
                <FileText className="h-3.5 w-3.5" />
                Free Download · Colorado-Specific
              </span>
              <h2 className="mt-4 text-3xl font-black leading-tight text-white sm:text-4xl">
                The <span className="text-gradient-gold">Colorado Local SEO Checklist</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-brand-cream/85 sm:text-lg">
                47 specific actions used by real Colorado Springs and Denver service businesses to
                rank in the Google Map Pack — written by Chris, the owner. No email required, no
                upsell, no AI-generated filler.
              </p>
              <ul className="mt-6 grid gap-2 text-sm text-brand-cream/85 sm:grid-cols-2">
                {[
                  "Google Business Profile (12 actions)",
                  "Reviews & reputation (7 actions)",
                  "On-page local SEO (9 actions)",
                  "Citations & NAP (6 actions)",
                  "Service-area pages (5 actions)",
                  "Tracking & reporting (8 actions)",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                <a
                  href={PDF_HREF}
                  download
                  className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                >
                  <Download className="h-5 w-5" />
                  Download the PDF (free)
                </a>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-cream/80">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand-gold" />
                  No email · No signup · Just the file
                </span>
              </div>
            </div>
            <div
              aria-hidden="true"
              className="relative hidden aspect-[8.5/11] w-56 shrink-0 -rotate-3 overflow-hidden rounded-lg bg-brand-cream shadow-elegant ring-1 ring-brand-gold/30 lg:block"
            >
              <div className="h-3 w-full bg-gradient-gold" />
              <div className="space-y-3 p-5">
                <div className="text-[8px] font-bold uppercase tracking-widest text-brand-gold-deep">
                  Colorado Web Impressions
                </div>
                <div className="text-base font-black leading-tight text-brand-black">
                  The Colorado Local SEO Checklist
                </div>
                <div className="space-y-1.5 pt-2">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-[2px] border border-brand-gold-deep" />
                      <span className="h-1.5 flex-1 rounded-full bg-foreground/15" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Compact variant — used at the bottom of every article
  return (
    <aside
      aria-label="Free Colorado Local SEO Checklist"
      className="mt-10 overflow-hidden rounded-2xl border border-brand-gold/40 bg-gradient-dark p-6 shadow-elegant sm:p-8"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-7">
        <div
          aria-hidden="true"
          className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-gradient-gold text-brand-black shadow-gold sm:h-16 sm:w-16"
        >
          <FileText className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand-gold">
            Free PDF · Colorado-Specific
          </div>
          <h3 className="mt-1.5 text-xl font-extrabold leading-tight text-white sm:text-2xl">
            Get the Colorado Local SEO Checklist
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-brand-cream/85">
            47 actions to rank in the Google Map Pack — by Chris, the owner. No email required.
          </p>
        </div>
        <a
          href={PDF_HREF}
          download
          className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-gradient-gold px-5 py-3 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5 sm:px-6 sm:py-3.5 sm:text-base"
        >
          <Download className="h-4 w-4 sm:h-5 sm:w-5" />
          Download
        </a>
      </div>
    </aside>
  );
}
